/**
 * PhoneInputWithCountry - Phone input with country code selector
 * Auto-detects country based on user's locale/timezone
 */
import React, { useState, useEffect, useRef } from 'react';
import { Input } from '../ui/input';
import { ChevronDown, Search } from 'lucide-react';

// Common country codes with flags
const COUNTRIES = [
  { code: 'FR', name: 'France', dial: '+33', flag: '🇫🇷' },
  { code: 'BE', name: 'Belgique', dial: '+32', flag: '🇧🇪' },
  { code: 'CH', name: 'Suisse', dial: '+41', flag: '🇨🇭' },
  { code: 'MC', name: 'Monaco', dial: '+377', flag: '🇲🇨' },
  { code: 'LU', name: 'Luxembourg', dial: '+352', flag: '🇱🇺' },
  { code: 'DE', name: 'Allemagne', dial: '+49', flag: '🇩🇪' },
  { code: 'IT', name: 'Italie', dial: '+39', flag: '🇮🇹' },
  { code: 'ES', name: 'Espagne', dial: '+34', flag: '🇪🇸' },
  { code: 'PT', name: 'Portugal', dial: '+351', flag: '🇵🇹' },
  { code: 'NL', name: 'Pays-Bas', dial: '+31', flag: '🇳🇱' },
  { code: 'GB', name: 'Royaume-Uni', dial: '+44', flag: '🇬🇧' },
  { code: 'US', name: 'États-Unis', dial: '+1', flag: '🇺🇸' },
  { code: 'CA', name: 'Canada', dial: '+1', flag: '🇨🇦' },
  { code: 'MA', name: 'Maroc', dial: '+212', flag: '🇲🇦' },
  { code: 'DZ', name: 'Algérie', dial: '+213', flag: '🇩🇿' },
  { code: 'TN', name: 'Tunisie', dial: '+216', flag: '🇹🇳' },
  { code: 'SN', name: 'Sénégal', dial: '+221', flag: '🇸🇳' },
  { code: 'CI', name: 'Côte d\'Ivoire', dial: '+225', flag: '🇨🇮' },
  { code: 'CM', name: 'Cameroun', dial: '+237', flag: '🇨🇲' },
  { code: 'AE', name: 'Émirats arabes unis', dial: '+971', flag: '🇦🇪' },
  { code: 'SA', name: 'Arabie Saoudite', dial: '+966', flag: '🇸🇦' },
  { code: 'QA', name: 'Qatar', dial: '+974', flag: '🇶🇦' },
  { code: 'CN', name: 'Chine', dial: '+86', flag: '🇨🇳' },
  { code: 'JP', name: 'Japon', dial: '+81', flag: '🇯🇵' },
  { code: 'KR', name: 'Corée du Sud', dial: '+82', flag: '🇰🇷' },
  { code: 'IN', name: 'Inde', dial: '+91', flag: '🇮🇳' },
  { code: 'BR', name: 'Brésil', dial: '+55', flag: '🇧🇷' },
  { code: 'MX', name: 'Mexique', dial: '+52', flag: '🇲🇽' },
  { code: 'AU', name: 'Australie', dial: '+61', flag: '🇦🇺' },
  { code: 'RU', name: 'Russie', dial: '+7', flag: '🇷🇺' },
];

// Map timezone to country code (basic)
const TIMEZONE_TO_COUNTRY = {
  'Europe/Paris': 'FR',
  'Europe/Brussels': 'BE',
  'Europe/Zurich': 'CH',
  'Europe/Monaco': 'MC',
  'Europe/Luxembourg': 'LU',
  'Europe/Berlin': 'DE',
  'Europe/Rome': 'IT',
  'Europe/Madrid': 'ES',
  'Europe/Lisbon': 'PT',
  'Europe/Amsterdam': 'NL',
  'Europe/London': 'GB',
  'America/New_York': 'US',
  'America/Los_Angeles': 'US',
  'America/Toronto': 'CA',
  'Africa/Casablanca': 'MA',
  'Africa/Algiers': 'DZ',
  'Africa/Tunis': 'TN',
  'Africa/Dakar': 'SN',
  'Africa/Abidjan': 'CI',
  'Africa/Douala': 'CM',
  'Asia/Dubai': 'AE',
  'Asia/Tokyo': 'JP',
  'Asia/Seoul': 'KR',
  'Asia/Shanghai': 'CN',
  'Asia/Kolkata': 'IN',
  'America/Sao_Paulo': 'BR',
  'America/Mexico_City': 'MX',
  'Australia/Sydney': 'AU',
};

const PhoneInputWithCountry = ({
  value = '',
  onChange,
  countryCode = '+33',
  onCountryCodeChange,
  placeholder = '6 12 34 56 78',
  className = '',
  required = false,
  disabled = false,
  'data-testid': testId = 'phone-input',
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const dropdownRef = useRef(null);

  // Find current country from dial code
  const currentCountry = COUNTRIES.find(c => c.dial === countryCode) || COUNTRIES[0];

  // Auto-detect country on mount
  useEffect(() => {
    if (!countryCode || countryCode === '+33') {
      try {
        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const detectedCode = TIMEZONE_TO_COUNTRY[timezone];
        if (detectedCode) {
          const country = COUNTRIES.find(c => c.code === detectedCode);
          if (country && onCountryCodeChange) {
            onCountryCodeChange(country.dial);
          }
        }
      } catch (e) {
        // Fallback to France if detection fails
      }
    }
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const filteredCountries = COUNTRIES.filter(c =>
    c.name.toLowerCase().includes(search.toLowerCase()) ||
    c.dial.includes(search) ||
    c.code.toLowerCase().includes(search.toLowerCase())
  );

  const handleSelectCountry = (country) => {
    if (onCountryCodeChange) {
      onCountryCodeChange(country.dial);
    }
    setIsOpen(false);
    setSearch('');
  };

  return (
    <div className="flex gap-2">
      {/* Country selector */}
      <div className="relative" ref={dropdownRef}>
        <button
          type="button"
          onClick={() => setIsOpen(!isOpen)}
          disabled={disabled}
          className="flex items-center gap-1 h-10 px-2 bg-[#0B0B0B] border border-[#E5E5E5]/20 rounded-md text-[#E5E5E5] hover:border-[#C9A24D]/50 transition-colors min-w-[90px]"
          data-testid={`${testId}-country-selector`}
        >
          <span className="text-lg">{currentCountry.flag}</span>
          <span className="text-sm font-mono">{currentCountry.dial}</span>
          <ChevronDown className={`w-3 h-3 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
        </button>

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute z-50 top-full left-0 mt-1 w-64 bg-[#1F1F1F] border border-[#C9A24D]/20 rounded-lg shadow-xl max-h-60 overflow-hidden">
            {/* Search */}
            <div className="p-2 border-b border-[#C9A24D]/10">
              <div className="relative">
                <Search className="absolute left-2 top-1/2 -translate-y-1/2 w-4 h-4 text-[#E5E5E5]/40" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="Rechercher un pays..."
                  className="w-full h-8 pl-8 pr-2 bg-[#0B0B0B] border border-[#E5E5E5]/20 rounded text-sm text-[#E5E5E5] placeholder:text-[#E5E5E5]/40 focus:outline-none focus:border-[#C9A24D]/50"
                  autoFocus
                />
              </div>
            </div>

            {/* Country list */}
            <div className="overflow-y-auto max-h-48">
              {filteredCountries.map((country) => (
                <button
                  key={country.code}
                  type="button"
                  onClick={() => handleSelectCountry(country)}
                  className={`w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-[#C9A24D]/10 ${
                    country.dial === countryCode ? 'bg-[#C9A24D]/20' : ''
                  }`}
                >
                  <span className="text-lg">{country.flag}</span>
                  <span className="text-sm text-[#E5E5E5] flex-1">{country.name}</span>
                  <span className="text-xs font-mono text-[#E5E5E5]/60">{country.dial}</span>
                </button>
              ))}
              {filteredCountries.length === 0 && (
                <p className="text-center text-[#E5E5E5]/40 text-sm py-3">Aucun résultat</p>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Phone number input */}
      <Input
        type="tel"
        value={value}
        onChange={(e) => onChange && onChange(e.target.value.replace(/[^0-9\s]/g, ''))}
        placeholder={placeholder}
        required={required}
        disabled={disabled}
        className={`flex-1 bg-[#0B0B0B] border-[#E5E5E5]/20 text-[#E5E5E5] focus:border-[#C9A24D] ${className}`}
        data-testid={testId}
      />
    </div>
  );
};

export default PhoneInputWithCountry;
